import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Image as ImageIcon, Mic, PenLine, Sparkles, Check, ArrowRight, ArrowLeft,
  X, Loader2, Copy, ExternalLink, Square, Gift, ShoppingBag, Upload, Volume2,
} from "lucide-react";
import { api, uploadMedia, mediaUrl, qrUrl, getGuestId } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/wp/Reveal";
import { PersonalizationExplainer } from "@/components/wp/PersonalizationExplainer";
import { useContent } from "@/lib/content";
import { toast } from "sonner";

const STEPS = [
  { key: "recipient", label: "Recipient", icon: Heart },
  { key: "photo", label: "Memory Photo", icon: ImageIcon },
  { key: "voice", label: "Voice Note", icon: Mic },
  { key: "message", label: "Your Message", icon: PenLine },
  { key: "finish", label: "Finishing Touch", icon: Sparkles },
];

const OCCASIONS = ["Birthday", "Anniversary", "Thank You", "Just Because", "Wedding", "Rakhi", "Congratulations", "Friendship", "Get Well Soon", "Miss You"];
const HANDWRITING = [
  { id: "classic", label: "Classic", cls: "hw-classic" },
  { id: "script", label: "Elegant Script", cls: "hw-script" },
  { id: "casual", label: "Casual", cls: "hw-casual" },
];

const hwClass = (id) => (HANDWRITING.find((h) => h.id === id)?.cls) || "hw-script";

/* ---------------- Live preview card ---------------- */
function GiftPreview({ recipientName, occasion, photoUrl, voiceUrl, letter, giftNote, handwriting }) {
  return (
    <div className="relative overflow-hidden rounded-wp-xl border border-border bg-white shadow-wp-card" data-testid="personalize-preview">
      <div className="wp-noise pointer-events-none absolute inset-0" />
      <div className="relative bg-wp-blush-50 px-5 pt-5 pb-4 text-center">
        <p className="wp-label text-[10px] text-wp-rose-strong">{occasion ? `Happy ${occasion}` : "A little something for you"}</p>
        <h3 className="mt-1 font-display text-2xl text-wp-ink">{recipientName || "Someone special"}</h3>
      </div>

      {photoUrl && (
        <div className="px-5 pt-4">
          <div className="overflow-hidden rounded-wp-lg border border-border">
            <img src={photoUrl} alt="Memory" className="aspect-[4/3] w-full object-cover" />
          </div>
        </div>
      )}

      <div className="px-5 py-4">
        {letter ? (
          <p className="whitespace-pre-line text-sm leading-relaxed text-wp-ink/90" style={{ fontFamily: "'Playfair Display', Georgia, serif", lineHeight: 1.7 }}>
            {letter}
          </p>
        ) : (
          <p className="text-sm italic text-wp-ink-muted">Your heartfelt message will appear here…</p>
        )}

        {giftNote && (
          <p className={`mt-4 text-2xl leading-snug text-wp-rose-strong ${hwClass(handwriting)}`}>{giftNote}</p>
        )}
      </div>

      {voiceUrl && (
        <div className="mx-5 mb-4 flex items-center gap-2 rounded-full border border-wp-mint/60 bg-wp-mint/20 px-3 py-1.5 text-xs font-medium text-wp-ocean">
          <Volume2 className="h-3.5 w-3.5" /> A voice message is included
        </div>
      )}

      <div className="flex items-center justify-center gap-1.5 border-t border-border bg-wp-cream-100/60 py-2.5 text-[11px] text-wp-ink-muted">
        <Gift className="h-3.5 w-3.5 text-wp-rose-strong" /> Made with love · Wonder Parcel
      </div>
    </div>
  );
}

export default function MakeItPersonal() {
  const navigate = useNavigate();
  const content = useContent();
  const explainer = content.personalization_explainer || {};
  const guestId = getGuestId();

  const [step, setStep] = useState(0);
  const [recipientName, setRecipientName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [occasion, setOccasion] = useState("");

  const [photo, setPhoto] = useState(null); // { assetId, url }
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInput = useRef(null);

  const [voice, setVoice] = useState(null); // { assetId, url }
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [recording, setRecording] = useState(false);
  const voiceInput = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [style] = useState("letter");
  const [tone] = useState("Heartfelt");
  const [details, setDetails] = useState("");
  const [letter, setLetter] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  const [handwriting, setHandwriting] = useState("script");
  const [giftNote, setGiftNote] = useState("");

  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null); // { token, giftUrl }

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  /* ---------- uploads ---------- */
  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    setUploadingPhoto(true);
    try {
      const data = await uploadMedia(file, "photo", guestId);
      setPhoto({ assetId: data.asset_id, url: mediaUrl(data.asset_id) });
      toast.success("Photo added 💝");
    } catch {
      toast.error("Could not upload the photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const uploadVoiceFile = async (file) => {
    setUploadingVoice(true);
    try {
      const data = await uploadMedia(file, "voice", guestId);
      setVoice({ assetId: data.asset_id, url: mediaUrl(data.asset_id) });
      toast.success("Voice note added 🎙️");
    } catch {
      toast.error("Could not upload the voice note. Please try a different file.");
    } finally {
      setUploadingVoice(false);
    }
  };

  const onVoicePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("audio/")) return toast.error("Please choose an audio file.");
    await uploadVoiceFile(file);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        const file = new File([blob], "voice-note.webm", { type: blob.type });
        await uploadVoiceFile(file);
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone unavailable — you can upload an audio file instead.");
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  /* ---------- AI letter ---------- */
  const generateLetter = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/ai/letter", {
        recipient_name: recipientName, relationship, occasion, tone, style, details,
      });
      setLetter(data.text || "");
      toast.success("Your message is ready ✨");
    } catch {
      toast.error("Could not write the message right now. Please try again.");
    } finally {
      setGenLoading(false);
    }
  };

  /* ---------- create gift page ---------- */
  const createGiftPage = async () => {
    if (!recipientName.trim()) {
      setStep(0);
      return toast.error("Please add the recipient's name first.");
    }
    setCreating(true);
    try {
      const { data } = await api.post("/personalizations", {
        guest_id: guestId,
        recipient_name: recipientName,
        relationship,
        occasion,
        tone,
        handwriting_style: handwriting,
        gift_note: giftNote,
        ai_letter: letter,
        photo_asset_id: photo?.assetId || "",
        voice_asset_id: voice?.assetId || "",
      });
      const giftUrl = `${window.location.origin}/gift/${data.token}`;
      localStorage.setItem("wp_personalization", JSON.stringify({ token: data.token, gift_url: giftUrl, recipient_name: recipientName }));
      setResult({ token: data.token, giftUrl });
      toast.success("Your personal gift page is live 🎀");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not create the gift page. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.giftUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy. Long-press the link to copy.");
    }
  };

  /* ================= RESULT VIEW ================= */
  if (result) {
    return (
      <div className="wp-container py-10 sm:py-14" data-testid="personalize-result">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <span className="wp-chip mx-auto"><Check className="h-3.5 w-3.5 text-wp-success" /> Gift page created</span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl">Your personal gift is ready 🎀</h1>
            <p className="mx-auto mt-2 max-w-xl text-wp-ink-muted">
              Print or share this little code. When {recipientName || "they"} scans it, a private page opens with your photo, voice and heartfelt message.
            </p>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-[300px_1fr] md:items-start">
            {/* QR + actions */}
            <div className="rounded-wp-xl border border-border bg-card p-6 text-center shadow-wp-card">
              <div className="mx-auto w-fit rounded-wp-lg border border-border bg-white p-3">
                <img src={qrUrl(result.giftUrl, 9)} alt="Your gift code" width={190} height={190} data-testid="personalize-qr-image" className="h-[190px] w-[190px]" />
              </div>
              <p className="mt-3 text-xs text-wp-ink-muted">Scan to open the gift page</p>
              <div className="mt-4 space-y-2">
                <Button onClick={copyLink} variant="outline" className="w-full rounded-xl" data-testid="personalize-copy-link-button">
                  <Copy className="mr-2 h-4 w-4" /> Copy link
                </Button>
                <a href={result.giftUrl} target="_blank" rel="noreferrer" data-testid="personalize-open-gift-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-wp-rose-strong px-4 py-2.5 text-sm font-semibold text-white transition hover:brightness-105">
                  <ExternalLink className="h-4 w-4" /> Open gift page
                </a>
                <a href={qrUrl(result.giftUrl, 12)} download="wonder-parcel-gift-qr.png"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-wp-ink transition hover:bg-wp-blush-50" data-testid="personalize-download-qr-button">
                  <Upload className="h-4 w-4 rotate-180" /> Download to print
                </a>
              </div>
            </div>

            {/* Preview */}
            <div>
              <GiftPreview recipientName={recipientName} occasion={occasion} photoUrl={photo?.url} voiceUrl={voice?.url} letter={letter} giftNote={giftNote} handwriting={handwriting} />
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Button onClick={() => navigate("/shop")} className="flex-1 rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-105" data-testid="personalize-continue-shopping-button">
                  <ShoppingBag className="mr-2 h-5 w-5" /> Pick a parcel to gift
                </Button>
                <Button onClick={() => { setResult(null); setStep(0); }} variant="outline" className="rounded-xl py-6" data-testid="personalize-create-another-button">
                  Create another
                </Button>
              </div>
              <p className="mt-3 text-center text-xs text-wp-ink-muted sm:text-left">
                This gift page is now saved to your order — add a parcel and it'll be attached automatically at checkout.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ================= WIZARD ================= */
  const CurrentIcon = STEPS[step].icon;

  return (
    <div className="wp-container py-8 sm:py-12" data-testid="make-it-personal-page">
      {/* Header */}
      <Reveal>
        <div className="max-w-2xl">
          <span className="wp-chip"><Heart className="h-3.5 w-3.5 text-wp-rose-strong" /> Make It Personal</span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em]">{content.personalize_heading}</h1>
          <p className="mt-3 text-wp-ink-muted">{content.personalize_subcopy}</p>
          <div className="mt-4 flex items-start gap-2.5 rounded-wp-md border border-wp-mint/50 bg-wp-mint/10 px-4 py-3 text-sm text-wp-ink" data-testid="personalize-surprise-note">
            <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-wp-rose-strong" />
            <span>{content.personalize_surprise_note}</span>
          </div>
        </div>
      </Reveal>

      {/* Optional lightweight explainer (Admin toggle in Content Manager) */}
      {explainer.enabled && (
        <Reveal>
          <PersonalizationExplainer heading={explainer.heading} steps={explainer.steps} className="mt-6" />
        </Reveal>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_minmax(300px,360px)]">
        {/* LEFT — steps */}
        <div className="rounded-wp-xl border border-border bg-card p-5 shadow-wp-card sm:p-7">
          {/* progress */}
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between text-xs text-wp-ink-muted">
              <span className="flex items-center gap-1.5 font-medium text-wp-ink"><CurrentIcon className="h-4 w-4 text-wp-rose-strong" /> {STEPS[step].label}</span>
              <span data-testid="personalize-step-indicator">Step {step + 1} of {STEPS.length}</span>
            </div>
            <Progress value={progress} className="h-1.5" data-testid="personalize-progress" />
          </div>

          {/* step dots */}
          <div className="mb-6 flex flex-wrap gap-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = i === step;
              const done = i < step;
              return (
                <button key={s.key} onClick={() => setStep(i)} data-testid={`personalize-step-${s.key}`}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-wp-rose-strong bg-wp-blush-100 text-wp-rose-strong" : done ? "border-wp-success/40 bg-wp-success/10 text-wp-success" : "border-border bg-white text-wp-ink-muted hover:bg-wp-blush-50"}`}>
                  {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />} {s.label}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
              {/* STEP 0 — recipient */}
              {step === 0 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="rn" className="mb-1.5 block text-sm">Who is this gift for? <span className="text-wp-rose-strong">*</span></Label>
                    <Input id="rn" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="e.g. Ananya" data-testid="personalize-recipient-name" className="rounded-wp-md" />
                  </div>
                  <div>
                    <Label htmlFor="rel" className="mb-1.5 block text-sm">Your relationship</Label>
                    <Input id="rel" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. best friend, sister, mom" data-testid="personalize-relationship" className="rounded-wp-md" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm">Occasion</Label>
                    <Select value={occasion} onValueChange={setOccasion}>
                      <SelectTrigger className="rounded-wp-md" data-testid="personalize-occasion"><SelectValue placeholder="Choose an occasion" /></SelectTrigger>
                      <SelectContent>
                        {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* STEP 1 — photo */}
              {step === 1 && (
                <div className="space-y-4">
                  <p className="text-sm text-wp-ink-muted">Add a favourite photo — we'll print it as a keepsake and show it on the gift page. <span className="text-wp-ink">(Optional)</span></p>
                  <input ref={photoInput} type="file" accept="image/*" hidden onChange={onPhotoPicked} data-testid="personalize-photo-input" />
                  {photo ? (
                    <div className="relative w-fit">
                      <img src={photo.url} alt="Memory" className="h-48 w-48 rounded-wp-lg border border-border object-cover shadow-wp-soft" />
                      <button onClick={() => setPhoto(null)} data-testid="personalize-remove-photo-button" aria-label="Remove photo"
                        className="absolute -right-2 -top-2 grid h-8 w-8 place-items-center rounded-full bg-white text-wp-ink shadow-wp-soft hover:text-wp-rose-strong"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => photoInput.current?.click()} disabled={uploadingPhoto} data-testid="personalize-photo-upload-button"
                      className="flex w-full flex-col items-center justify-center gap-2 rounded-wp-lg border-2 border-dashed border-border bg-wp-blush-50 px-6 py-12 text-wp-ink-muted transition-colors hover:border-wp-rose-strong hover:text-wp-rose-strong">
                      {uploadingPhoto ? <Loader2 className="h-8 w-8 animate-spin" /> : <ImageIcon className="h-8 w-8" />}
                      <span className="text-sm font-medium">{uploadingPhoto ? "Adding…" : "Tap to add a photo"}</span>
                      <span className="text-xs">JPG, PNG or WEBP · up to 10MB</span>
                    </button>
                  )}
                </div>
              )}

              {/* STEP 2 — voice */}
              {step === 2 && (
                <div className="space-y-4">
                  <p className="text-sm text-wp-ink-muted">Record or add a short voice message — they'll be able to scan and hear it on their private gift page. <span className="text-wp-ink">(Optional)</span></p>
                  <input ref={voiceInput} type="file" accept="audio/*" hidden onChange={onVoicePicked} data-testid="personalize-voice-input" />
                  {voice ? (
                    <div className="space-y-3 rounded-wp-lg border border-border bg-wp-blush-50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-wp-ink"><Volume2 className="h-4 w-4 text-wp-ocean" /> Voice note added</div>
                      <audio src={voice.url} controls className="w-full" data-testid="personalize-voice-audio" />
                      <button onClick={() => setVoice(null)} data-testid="personalize-remove-voice-button" className="text-xs font-medium text-wp-rose-strong hover:underline">Remove &amp; add another</button>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {recording ? (
                        <button onClick={stopRecording} data-testid="personalize-stop-recording-button"
                          className="flex flex-col items-center justify-center gap-2 rounded-wp-lg border-2 border-wp-rose-strong bg-wp-blush-100 px-4 py-8 text-wp-rose-strong">
                          <span className="relative grid h-10 w-10 place-items-center rounded-full bg-wp-rose-strong text-white">
                            <Square className="h-4 w-4" />
                            <span className="absolute inset-0 animate-ping rounded-full bg-wp-rose-strong/40" />
                          </span>
                          <span className="text-sm font-medium">Recording… tap to stop</span>
                        </button>
                      ) : (
                        <button onClick={startRecording} disabled={uploadingVoice} data-testid="personalize-voice-record-button"
                          className="flex flex-col items-center justify-center gap-2 rounded-wp-lg border-2 border-dashed border-border bg-white px-4 py-8 text-wp-ink-muted transition-colors hover:border-wp-rose-strong hover:text-wp-rose-strong">
                          <Mic className="h-8 w-8" /><span className="text-sm font-medium">Record now</span>
                        </button>
                      )}
                      <button onClick={() => voiceInput.current?.click()} disabled={uploadingVoice || recording} data-testid="personalize-voice-upload-button"
                        className="flex flex-col items-center justify-center gap-2 rounded-wp-lg border-2 border-dashed border-border bg-white px-4 py-8 text-wp-ink-muted transition-colors hover:border-wp-rose-strong hover:text-wp-rose-strong">
                        {uploadingVoice ? <Loader2 className="h-8 w-8 animate-spin" /> : <Upload className="h-8 w-8" />}
                        <span className="text-sm font-medium">{uploadingVoice ? "Adding…" : "Add audio file"}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 3 — message */}
              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="det" className="mb-1.5 block text-sm">Tell us a little about {recipientName || "the recipient"}</Label>
                    <Textarea id="det" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="e.g. loves sunflowers, we've been friends since school, always makes me laugh…" data-testid="personalize-details" className="rounded-wp-md" />
                    <p className="mt-1.5 text-xs text-wp-ink-muted">Share a memory or two and we'll craft a beautiful, heartfelt message for you.</p>
                  </div>
                  <Button onClick={generateLetter} disabled={genLoading} className="w-full rounded-xl bg-wp-ocean py-5 text-white hover:brightness-105" data-testid="personalize-generate-letter-button">
                    {genLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    {genLoading ? "Writing…" : letter ? "Write another version" : "Write my message"}
                  </Button>
                  {letter && (
                    <div>
                      <Label htmlFor="ltr" className="mb-1.5 block text-sm">Your message (edit freely)</Label>
                      <Textarea id="ltr" rows={6} value={letter} onChange={(e) => setLetter(e.target.value)} data-testid="personalize-letter-text" className="rounded-wp-md" />
                      <button onClick={generateLetter} disabled={genLoading} className="mt-2 text-sm font-medium text-wp-ocean hover:underline disabled:opacity-50" data-testid="personalize-regenerate-button">Regenerate</button>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4 — finishing */}
              {step === 4 && (
                <div className="space-y-5">
                  <div>
                    <Label className="mb-1.5 block text-sm">Handwriting style for your short note</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {HANDWRITING.map((h) => (
                        <button key={h.id} onClick={() => setHandwriting(h.id)} data-testid={`personalize-handwriting-${h.id}`}
                          className={`rounded-wp-md border px-2 py-3 transition-colors ${handwriting === h.id ? "border-wp-rose-strong bg-wp-blush-100" : "border-border bg-white hover:bg-wp-blush-50"}`}>
                          <span className={`block text-2xl leading-none text-wp-ink ${h.cls}`}>Aa</span>
                          <span className="mt-1.5 block text-[11px] font-medium text-wp-ink-muted">{h.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="note" className="mb-1.5 block text-sm">Short handwritten note (on the gift card)</Label>
                    <Textarea id="note" rows={3} value={giftNote} onChange={(e) => setGiftNote(e.target.value)} placeholder="e.g. To the moon and back, always. 💛" data-testid="personalize-gift-note" className="rounded-wp-md" />
                  </div>
                  <div className="rounded-wp-lg border border-wp-mint/50 bg-wp-mint/10 p-4 text-sm text-wp-ink">
                    <p className="font-medium">Almost done!</p>
                    <p className="mt-0.5 text-wp-ink-muted">Create your private gift page and get a little code you can print or share.</p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* nav */}
          <div className="mt-7 flex items-center justify-between gap-3 border-t border-border pt-5">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="personalize-back-button" className="rounded-xl">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="personalize-next-button" className="rounded-xl bg-wp-rose-strong text-white hover:brightness-105">
                Continue <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={createGiftPage} disabled={creating} data-testid="personalize-create-button" className="rounded-xl bg-wp-rose-strong px-6 text-white hover:brightness-105">
                {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {creating ? "Creating…" : "Create gift page"}
              </Button>
            )}
          </div>
        </div>

        {/* RIGHT — live preview */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-medium text-wp-ink-muted"><Sparkles className="h-3.5 w-3.5 text-wp-ocean" /> Live preview</p>
          <GiftPreview recipientName={recipientName} occasion={occasion} photoUrl={photo?.url} voiceUrl={voice?.url} letter={letter} giftNote={giftNote} handwriting={handwriting} />
        </div>
      </div>
    </div>
  );
}
